/**
 * THE SHOWROOM v4.0 — MOBILE COMMAND BAR
 * The Command Port: behavior layer
 *
 * Architecture:
 *   - Reads category/dimension/filter data from existing desktop filter bar DOM
 *   - Triggers existing .category-btn clicks to drive showroom-v3_5.js
 *   - Observes the desktop filter bar's active states via MutationObserver
 *     to stay in sync without duplicating filter logic
 *   - All behavior wrapped in isMobile() guard — zero desktop impact
 *
 * Dependencies:
 *   - showroom-v3_5.js must be loaded first (drives showCollection, filter state)
 *   - showroom-command-bar.liquid (DOM structure)
 *   - showroom-command-bar.css (styles)
 */

(function() {
  'use strict';

  /* ============================================================
     GUARD: Only run on mobile
     ============================================================ */
  function isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  if (!isMobile()) return;

  // Re-check on resize in case someone rotates device
  window.addEventListener('resize', function() {
    if (!isMobile()) return;
  });

  /* ============================================================
     DOM REFERENCES
     ============================================================ */
  // Top bar
  const cbTopBar        = document.getElementById('cbTopBar');
  const cbCategoryName  = document.getElementById('cbCategoryName');
  const cbCartBtn       = document.getElementById('cbCartBtn');
  const cbCartTotal     = document.getElementById('cbCartTotal');
  const cbCartBadge     = document.getElementById('cbCartBadge');
  const cbCheckoutBtn   = document.getElementById('cbCheckoutBtn');
  const cbCountNumber   = document.getElementById('cbCountNumber');
  const cbFilterSummary = document.getElementById('cbFilterSummary');
  const cbFilterCount   = document.getElementById('cbFilterCount');
  const cbClearAll      = document.getElementById('cbClearAll');
  const cbTopRow3       = document.getElementById('cbTopRow3');
  const cbActivePills   = document.getElementById('cbActivePills');
  const cbPillsExpand   = document.getElementById('cbPillsExpand');

  // Bottom bar
  const cbBottomBar  = document.getElementById('cbBottomBar');
  const cbRow2       = document.getElementById('cbRow2');
  const cbRow3       = document.getElementById('cbRow3');
  const cbDimsScroll = document.getElementById('cbDimsScroll');
  const cbPillsScroll= document.getElementById('cbPillsScroll');
  const cbCatsScroll = document.getElementById('cbCatsScroll');

  // Toast
  const cbToast     = document.getElementById('cbToast');
  const cbToastText = document.getElementById('cbToastText');

  // Desktop filter bar (source of truth for state)
  const desktopFilterBar = document.querySelector('.filter-bar-enhanced');

  if (!cbBottomBar || !desktopFilterBar) return;

  /* ============================================================
     STATE
     ============================================================ */
  let activeDimension    = null;   // currently open dimension tab key
  let row3Collapsed      = false;  // scroll-collapsed state
  let scrollAccumulator  = 0;
  let lastScrollY        = window.scrollY;
  let toastTimer         = null;
  let pillsExpanded      = false;
  const MAX_PILLS_COLLAPSED = 3;

  /* ============================================================
     CATEGORY LABELS (friendly names for top bar display)
     ============================================================ */
  const CATEGORY_LABELS = {
    'new-here':                     'New Here?',
    'ingredient-guide':             'Ingredient Guide',
    'departure-lounge':             'The Departure Lounge',
    'loose-leaf-tea-canada':        'Loose Leaf Tea',
    'herbal-wellness-loose-leaf-tea':'Herbal & Wellness',
    'spices-herbs-seasonings':      'Spices, Blends & Rubs',
    'teaware-gifts':                'Teaware & Gifts',
    'cart':                         'Your Cart',
    'shipping-zones':               'Shipping'
  };

  /* ============================================================
     HAPTIC FEEDBACK
     ============================================================ */
  function haptic(ms) {
    if (navigator.vibrate) {
      try { navigator.vibrate(ms); } catch(e) {}
    }
  }

  /* ============================================================
     BOTTOM BAR HEIGHT TRACKER
     Updates --cb-bar-height CSS variable so grid padding stays correct
     ============================================================ */
  let __barHeightRafPending = false;
  function updateBarHeight() {
    if (!cbBottomBar) return;
    if (__barHeightRafPending) return;
    __barHeightRafPending = true;
    requestAnimationFrame(function() {
      __barHeightRafPending = false;
      const h = cbBottomBar.offsetHeight;
      document.documentElement.style.setProperty('--cb-bar-height', h + 'px');
      if (cbToast) {
        cbToast.style.bottom = (h + 8) + 'px';
      }
    });
  }

  /* ============================================================
     CATEGORY TABS — Row 1
     Mirrors the desktop .category-btn by clicking it
     ============================================================ */
  document.querySelectorAll('.cb-cat-btn').forEach(function(cbBtn) {
    cbBtn.addEventListener('click', function() {
      const collection = this.dataset.cbCollection;
      haptic(15);

      // Find and click the matching desktop category button
      const desktopBtn = desktopFilterBar.querySelector(
        '.category-btn[data-collection="' + collection + '"]'
      );
      if (desktopBtn) {
        desktopBtn.click();
      }

      // Reset Row 3 collapse state on category switch
      row3Collapsed = false;
      scrollAccumulator = 0;
      activeDimension = null;

      // Scroll search bar to top of viewport, below sticky top bar
      setTimeout(function() {
        var searchBar = document.getElementById('showroomSearch');
        var topBar = document.getElementById('cbTopBar');
        if (searchBar) {
          var offset = topBar ? topBar.offsetHeight : 0;
          window.scrollTo(0, searchBar.offsetTop - offset);
        }
      }, 200);
    });
  });

  /* ============================================================
     SYNC CATEGORY ACTIVE STATE
     Reads from desktop filter bar and mirrors to command bar
     ============================================================ */
  function syncCategoryState() {
    const activeDesktopBtn = desktopFilterBar.querySelector('.category-btn.active');
    if (!activeDesktopBtn) return;

    const collection = activeDesktopBtn.dataset.collection;

    // Update command bar category buttons
    document.querySelectorAll('.cb-cat-btn').forEach(function(btn) {
      const isActive = btn.dataset.cbCollection === collection;
      btn.classList.toggle('cb-cat-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Update top bar category name
    if (cbCategoryName) {
      cbCategoryName.textContent = CATEGORY_LABELS[collection] || collection;
    }

    // Auto-scroll active category into view (deferred to avoid forced reflow)
    const activeCbBtn = document.querySelector('.cb-cat-btn.cb-cat-active');
    if (activeCbBtn && cbCatsScroll) {
      requestAnimationFrame(function() {
        const btnLeft   = activeCbBtn.offsetLeft;
        const btnWidth  = activeCbBtn.offsetWidth;
        const scrollW   = cbCatsScroll.offsetWidth;
        cbCatsScroll.scrollTo({
          left: btnLeft - (scrollW / 2) + (btnWidth / 2),
          behavior: 'smooth'
        });
      });
    }

    // Build Row 2 dimensions for this collection
    buildDimensionRow(collection);

    // Delay count sync to let v3's showCollection() finish its 150ms card update
    setTimeout(function() {
      syncProductCount();
    }, 300);
  }

  /* ============================================================
     DIMENSION ROW — Row 2
     Reads dimension tabs from desktop filter bar for current collection
     ============================================================ */
  function buildDimensionRow(collection) {
    if (!cbDimsScroll || !cbRow2) return;

    // Find the desktop sub-filter panel for this collection
    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );

    cbDimsScroll.innerHTML = '';
    activeDimension = null;

    if (!desktopPanel) {
      cbRow2.classList.remove('cb-row-visible');
      cbRow3.classList.remove('cb-row-visible');
      updateBarHeight();
      return;
    }

    const desktopDimTabs = desktopPanel.querySelectorAll('.dimension-tab');
    if (desktopDimTabs.length === 0) {
      cbRow2.classList.remove('cb-row-visible');
      cbRow3.classList.remove('cb-row-visible');
      updateBarHeight();
      return;
    }

    // Build command bar dimension tabs
    desktopDimTabs.forEach(function(desktopTab) {
      const dimension = desktopTab.dataset.dimension;
      const label     = desktopTab.textContent.trim();
      const isActive  = desktopTab.classList.contains('active');

      const btn = document.createElement('button');
      btn.className = 'cb-dim-tab' + (isActive ? ' cb-dim-active' : '');
      btn.dataset.cbDimension = dimension;
      btn.setAttribute('aria-pressed', isActive);
      btn.setAttribute('aria-label', label + ' filter dimension');
      btn.textContent = label;

      // Badge for active filter count in this dimension
      const badge = document.createElement('span');
      badge.className = 'cb-dim-badge';
      badge.style.display = 'none';
      btn.appendChild(badge);

      btn.addEventListener('click', function() {
        haptic(8);
        // Click the matching desktop dimension tab
        desktopTab.click();
        // Expand Row 3 if collapsed
        row3Collapsed = false;
        scrollAccumulator = 0;
        cbRow3.classList.remove('cb-row3-collapsed');
        // Sync immediately
        syncDimensionState(collection);
      });

      cbDimsScroll.appendChild(btn);

      if (isActive) {
        activeDimension = dimension;
      }
    });

    cbRow2.classList.add('cb-row-visible');

    // Build initial Row 3
    if (activeDimension) {
      buildPillRow(collection, activeDimension);
    } else {
      cbRow3.classList.remove('cb-row-visible');
    }

    updateBarHeight();
  }

  /* ============================================================
     SYNC DIMENSION STATE
     Re-reads desktop active dimension tab and rebuilds Row 3
     ============================================================ */
  function syncDimensionState(collection) {
    if (!cbDimsScroll) return;

    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );
    if (!desktopPanel) return;

    const activeDesktopTab = desktopPanel.querySelector('.dimension-tab.active');
    const activeDim = activeDesktopTab ? activeDesktopTab.dataset.dimension : null;

    // Update cb dim tab active states
    document.querySelectorAll('.cb-dim-tab').forEach(function(btn) {
      const isActive = btn.dataset.cbDimension === activeDim;
      btn.classList.toggle('cb-dim-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    activeDimension = activeDim;

    if (activeDim) {
      buildPillRow(collection, activeDim);
      cbRow3.classList.add('cb-row-visible');
    } else {
      cbRow3.classList.remove('cb-row-visible');
    }

    updateBarHeight();
  }

  /* ============================================================
     FILTER PILL ROW — Row 3
     Reads sub-filter buttons from desktop for active dimension
     ============================================================ */
  function buildPillRow(collection, dimension) {
    if (!cbPillsScroll) return;

    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );
    if (!desktopPanel) {
      cbPillsScroll.innerHTML = '';
      return;
    }

    // Find options panel for this dimension
    const optionsPanel = desktopPanel.querySelector(
      '.dimension-options[data-for-dimension="' + dimension + '"]'
    );
    if (!optionsPanel) {
      cbPillsScroll.innerHTML = '';
      return;
    }

    const desktopPills = optionsPanel.querySelectorAll('.sub-filter-btn');
    cbPillsScroll.innerHTML = '';

    // Detect single-select collection (herbal diagnostic uses single-select)
    const isSingleSelect = collection === 'herbal-wellness-loose-leaf-tea';

    desktopPills.forEach(function(desktopPill) {
      const value   = desktopPill.dataset.value;
      const label   = desktopPill.dataset.label || desktopPill.textContent.trim();
      const isActive= desktopPill.classList.contains('active');

      // Pull product count from desktop button's rendered label
      var countSuffix = '';
      var labelSpan = desktopPill.querySelector('.sub-filter-label');
      if (labelSpan) {
        var match = labelSpan.textContent.match(/\((\d+)\)/);
        if (match) countSuffix = ' (' + match[1] + ')';
      }

      const pill = document.createElement('button');
      pill.className = 'cb-filter-pill'
        + (isActive ? ' cb-pill-active' : '')
        + (isSingleSelect ? ' cb-pill-single' : '');
      pill.dataset.cbValue = value;
      pill.dataset.cbDimension = dimension;
      pill.setAttribute('aria-pressed', isActive);
      pill.setAttribute('aria-label', label);
      pill.textContent = label + countSuffix;

      pill.addEventListener('click', function() {
        haptic(10);
        // Click the desktop pill — this drives all filter logic in v3_5
        desktopPill.click();
        // Sync pill states after a tick (let v3_5 update DOM first)
        requestAnimationFrame(function() {
          syncPillStates(collection, dimension);
          syncFilterSummary();
          syncActivePills();
          animateProductCount();
        });

        // Scroll search bar to top of viewport, below sticky top bar
        setTimeout(function() {
            var searchBar = document.getElementById('showroomSearch');
            var topBar = document.getElementById('cbTopBar');
            if (searchBar) {
            var offset = topBar ? topBar.offsetHeight : 0;
            window.scrollTo(0, searchBar.offsetTop - offset);
            }
        }, 200);;
      });

      cbPillsScroll.appendChild(pill);
    });

    cbRow3.classList.add('cb-row-visible');
    updateBarHeight();
  }

  /* ============================================================
     SYNC PILL ACTIVE STATES
     Re-reads desktop sub-filter buttons to mirror active state
     ============================================================ */
  function syncPillStates(collection, dimension) {
    if (!cbPillsScroll) return;

    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );
    if (!desktopPanel) return;

    const optionsPanel = desktopPanel.querySelector(
      '.dimension-options[data-for-dimension="' + dimension + '"]'
    );
    if (!optionsPanel) return;

    const desktopPills = optionsPanel.querySelectorAll('.sub-filter-btn');
    const cbPills      = cbPillsScroll.querySelectorAll('.cb-filter-pill');

    cbPills.forEach(function(cbPill, i) {
      if (desktopPills[i]) {
        const isActive = desktopPills[i].classList.contains('active');
        cbPill.classList.toggle('cb-pill-active', isActive);
        cbPill.setAttribute('aria-pressed', isActive);

        // Refresh count from desktop button's updated label
        var baseLabel = desktopPills[i].dataset.label || desktopPills[i].textContent.trim();
        var countSuffix = '';
        var labelSpan = desktopPills[i].querySelector('.sub-filter-label');
        if (labelSpan) {
          var match = labelSpan.textContent.match(/\((\d+)\)/);
          if (match) countSuffix = ' (' + match[1] + ')';
        }
        cbPill.textContent = baseLabel + countSuffix;
      }
    });

    // Update dimension badge count
    updateDimensionBadges(collection);
  }

  /* ============================================================
     DIMENSION BADGES
     Shows count of active filters per dimension tab
     ============================================================ */
  function updateDimensionBadges(collection) {
    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );
    if (!desktopPanel) return;

    document.querySelectorAll('.cb-dim-tab').forEach(function(dimTab) {
      const dim = dimTab.dataset.cbDimension;
      const optPanel = desktopPanel.querySelector('.dimension-options[data-for-dimension="' + dim + '"]');
      if (!optPanel) return;

      const activeCount = optPanel.querySelectorAll('.sub-filter-btn.active').length;
      const badge = dimTab.querySelector('.cb-dim-badge');
      if (badge) {
        if (activeCount > 0) {
          badge.style.display = 'flex';
          badge.textContent = activeCount;
        } else {
          badge.style.display = 'none';
        }
      }
    });
  }

  /* ============================================================
     PRODUCT COUNT — Top bar Row 2
     Reads from the product grid directly
     ============================================================ */
  function syncProductCount() {
    if (!cbCountNumber) return;
    const visibleCards = document.querySelectorAll('.product-card:not(.hidden)');
    const count = visibleCards.length;
    cbCountNumber.textContent = count;
  }

  function animateProductCount() {
    if (!cbCountNumber) return;
    syncProductCount();
    cbCountNumber.classList.add('cb-count-flash');
    setTimeout(function() {
      cbCountNumber.classList.remove('cb-count-flash');
    }, 150);
  }

  /* ============================================================
     FILTER SUMMARY — Top bar Row 2
     Shows active filter count + clear all
     ============================================================ */
  function syncFilterSummary() {
    // Count active sub-filter buttons across all desktop panels
    const activeFilters = desktopFilterBar.querySelectorAll('.sub-filter-btn.active');
    const count = activeFilters.length;

    if (count > 0) {
      cbFilterSummary.style.display = 'flex';
      cbFilterCount.textContent = count + ' filter' + (count > 1 ? 's' : '');
    } else {
      cbFilterSummary.style.display = 'none';
    }
  }

  /* ============================================================
     ACTIVE FILTER PILLS — Top bar Row 3
     Shows applied filters as removable pills
     ============================================================ */
  function syncActivePills() {
    if (!cbActivePills) return;

    const activeDesktopBtns = desktopFilterBar.querySelectorAll('.sub-filter-btn.active');
    cbActivePills.innerHTML = '';
    pillsExpanded = false;

    if (activeDesktopBtns.length === 0) {
      cbTopRow3.style.display = 'none';
      updateBarHeight();
      return;
    }

    cbTopRow3.style.display = 'flex';

    const showCount = pillsExpanded ? activeDesktopBtns.length : Math.min(activeDesktopBtns.length, MAX_PILLS_COLLAPSED);
    const overflow  = activeDesktopBtns.length - MAX_PILLS_COLLAPSED;

    for (let i = 0; i < showCount; i++) {
      const desktopBtn = activeDesktopBtns[i];
      const pill = document.createElement('span');
      pill.className = 'cb-active-pill';
      var pillLabel = desktopBtn.dataset.label || desktopBtn.textContent.trim();
      pill.innerHTML = pillLabel + ' <span class="cb-pill-remove" aria-hidden="true">×</span>';
      pill.setAttribute('role', 'button');
      pill.setAttribute('aria-label', 'Remove filter: ' + pillLabel);
      pill.addEventListener('click', function() {
        haptic(10);
        desktopBtn.click();
        requestAnimationFrame(function() {
          syncFilterSummary();
          syncActivePills();
          animateProductCount();
          const activeCollection = getActiveCollection();
          if (activeCollection && activeDimension) {
            syncPillStates(activeCollection, activeDimension);
          }
        });
      });
      cbActivePills.appendChild(pill);
    }

    if (overflow > 0 && !pillsExpanded) {
      cbPillsExpand.style.display = 'inline-flex';
      cbPillsExpand.textContent = '+' + overflow + ' more';
      cbPillsExpand.onclick = function() {
        pillsExpanded = true;
        syncActivePills();
      };
    } else {
      cbPillsExpand.style.display = 'none';
    }

    updateBarHeight();
  }

  /* ============================================================
     CLEAR ALL
     ============================================================ */
  if (cbClearAll) {
    cbClearAll.addEventListener('click', function() {
      haptic(15);
      // Click the desktop clear all button
      const desktopClear = desktopFilterBar.querySelector('.clear-all-filters');
      if (desktopClear) {
       desktopClear.click();
      } else {
        desktopFilterBar.querySelectorAll('.sub-filter-btn.active').forEach(function(btn) {
          btn.click();
        });
      }
      requestAnimationFrame(function() {
        syncFilterSummary();
        syncActivePills();
        animateProductCount();
        const activeCollection = getActiveCollection();
        if (activeCollection && activeDimension) {
          syncPillStates(activeCollection, activeDimension);
          updateDimensionBadges(activeCollection);
        }
      });
    });
  }

  /* ============================================================
     CART SYNC
     Mirrors desktop cart button state
     ============================================================ */
  function syncCartState() {
    // Read from the desktop cart button
    const desktopCartTotal = document.getElementById('cartTotalAction');
    const desktopCartBadge = document.getElementById('cartBadgeAction');

    if (desktopCartTotal && desktopCartBadge) {
      const total = desktopCartTotal.textContent;
      const count = parseInt(desktopCartBadge.textContent) || 0;

      if (count > 0) {
        cbCartTotal.textContent = total;
        cbCartTotal.style.display = 'inline';
        cbCartBadge.textContent = count;
        cbCartBadge.style.display = 'flex';
        // Activate checkout button
        cbCheckoutBtn.classList.remove('cb-checkout-muted');
        cbCheckoutBtn.classList.add('cb-checkout-active');
      } else {
        cbCartTotal.style.display = 'none';
        cbCartBadge.style.display = 'none';
        cbCheckoutBtn.classList.add('cb-checkout-muted');
        cbCheckoutBtn.classList.remove('cb-checkout-active');
      }
    }
  }

  // Cart button opens desktop cart drawer
  if (cbCartBtn) {
    cbCartBtn.addEventListener('click', function() {
      haptic(15);
      const desktopViewCart = document.getElementById('viewCartBtn');
      if (desktopViewCart) desktopViewCart.click();
    });
  }

  /* ============================================================
     ADD-TO-CART TOAST
     Intercepts cart additions and shows confirmation toast
     ============================================================ */
  function showToast(productName) {
    if (!cbToast) return;
    clearTimeout(toastTimer);
    cbToastText.textContent = (productName || 'Item') + ' added';
    cbToast.classList.add('cb-toast-visible');
    // Bump cart badge
    cbCartBadge.classList.add('cb-badge-bump');
    setTimeout(function() { cbCartBadge.classList.remove('cb-badge-bump'); }, 200);
    toastTimer = setTimeout(function() {
      cbToast.classList.remove('cb-toast-visible');
    }, 2000);
  }

  // Listen for add-to-cart clicks on product cards
  document.addEventListener('click', function(e) {
    const addBtn = e.target.closest('.add-to-cart-btn, [data-action="add-to-cart"], .product-card-atc');
    if (!addBtn || !isMobile()) return;
    haptic(15);
    const card = addBtn.closest('.product-card');
    const productName = card ? (card.querySelector('.product-card-name, .product-name, h3, h4') || {}).textContent : '';
    setTimeout(function() {
      syncCartState();
      showToast(productName ? productName.trim() : 'Item');
    }, 400); // small delay to let Shopify process the cart
  });

  /* ============================================================
     SCROLL-AWARE ROW 3 AUTO-COLLAPSE
     Row 3 retracts after 150px downward scroll
     ============================================================ */
  window.addEventListener('scroll', function() {
    if (!isMobile()) return;
    const currentY = window.scrollY;
    const delta = currentY - lastScrollY;
    lastScrollY = currentY;

    if (delta > 0) {
      // Scrolling down
      scrollAccumulator += delta;
      if (scrollAccumulator > 150 && !row3Collapsed && cbRow3.classList.contains('cb-row-visible')) {
        row3Collapsed = true;
        cbRow3.classList.add('cb-row3-collapsed');
        updateBarHeight();
      }
    } else {
      // Scrolling up — reset accumulator
      scrollAccumulator = 0;
    }
  }, { passive: true });

  /* ============================================================
     MUTATION OBSERVER
     Watches desktop filter bar for state changes (active class, etc.)
     This keeps command bar in sync when v3_5.js changes state
     ============================================================ */
  const observer = new MutationObserver(function(mutations) {
    let needsCategorySync = false;
    let needsFilterSync   = false;

    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target;
        if (target.classList.contains('category-btn')) {
          needsCategorySync = true;
        }
        if (target.classList.contains('sub-filter-btn')) {
          needsFilterSync = true;
        }
      }
    });

    if (needsCategorySync) {
      syncCategoryState();
    }

    if (needsFilterSync) {
      const activeCollection = getActiveCollection();
      if (activeCollection) {
        syncFilterSummary();
        syncActivePills();
        if (activeDimension) {
          syncPillStates(activeCollection, activeDimension);
        }
      }
      animateProductCount();
    }
  });

  if (desktopFilterBar) {
    observer.observe(desktopFilterBar, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'aria-pressed']
    });
  }

  // Also observe product grid for count changes
  const grid = document.querySelector('#showroomGrid, .showroom-grid');
  if (grid) {
    const gridObserver = new MutationObserver(function() {
      syncProductCount();
    });
    gridObserver.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* ============================================================
     HELPERS
     ============================================================ */
  function getActiveCollection() {
    const activeBtn = desktopFilterBar.querySelector('.category-btn.active');
    return activeBtn ? activeBtn.dataset.collection : null;
  }

  /* ============================================================
     SMART DIMENSION ORDERING
     Dimensions with active filters sort to front
     Remaining dimensions sort by cardinality (most options first)
     ============================================================ */
  function smartSortDimensions(collection) {
    if (!cbDimsScroll) return;
    const dimTabs = Array.from(cbDimsScroll.querySelectorAll('.cb-dim-tab'));
    if (dimTabs.length === 0) return;

    const desktopPanel = desktopFilterBar.querySelector(
      '.sub-filters-enhanced[data-parent="' + collection + '"]'
    );
    if (!desktopPanel) return;

    dimTabs.sort(function(a, b) {
      const aDim = a.dataset.cbDimension;
      const bDim = b.dataset.cbDimension;

      // Active filters first
      const aPanel = desktopPanel.querySelector('.dimension-options[data-for-dimension="' + aDim + '"]');
      const bPanel = desktopPanel.querySelector('.dimension-options[data-for-dimension="' + bDim + '"]');

      const aActive = aPanel ? aPanel.querySelectorAll('.sub-filter-btn.active').length : 0;
      const bActive = bPanel ? bPanel.querySelectorAll('.sub-filter-btn.active').length : 0;

      if (aActive !== bActive) return bActive - aActive;

      // Then by cardinality (number of options)
      const aCount = aPanel ? aPanel.querySelectorAll('.sub-filter-btn').length : 0;
      const bCount = bPanel ? bPanel.querySelectorAll('.sub-filter-btn').length : 0;
      return bCount - aCount;
    });

    // Re-append in sorted order
    dimTabs.forEach(function(tab) {
      cbDimsScroll.appendChild(tab);
    });
  }

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    if (!isMobile()) return;

    // Initial state sync
    syncCategoryState();
    syncProductCount();
    syncFilterSummary();
    syncActivePills();
    syncCartState();
    updateBarHeight();

    // Back to top button
    const cbScrollTop = document.getElementById('cbScrollTop');
    if (cbScrollTop) {
      cbScrollTop.addEventListener('click', function() {
        haptic(15);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    // View toggle (grid ↔ horizontal)
    const viewToggle = document.getElementById('cbViewToggle');
    if (viewToggle) {
      const grid = document.getElementById('productGrid');
      const toggleBtns = viewToggle.querySelectorAll('.showroom-view-toggle-btn');
      
      // Restore saved preference
      const saved = sessionStorage.getItem('showroom-view-mode');
      if (saved === 'list' && grid) {
        grid.classList.add('product-grid--horizontal');
        toggleBtns.forEach(function(b) {
          b.classList.toggle('active', b.dataset.view === 'list');
        });
      }

      toggleBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          haptic(10);
          const mode = this.dataset.view;
          toggleBtns.forEach(function(b) {
            b.classList.toggle('active', b.dataset.view === mode);
          });
          if (grid) {
            if (mode === 'list') {
              grid.classList.add('product-grid--horizontal');
            } else {
              grid.classList.remove('product-grid--horizontal');
            }
          }
          sessionStorage.setItem('showroom-view-mode', mode);
          // Re-calculate header font sizes for new card width
          requestAnimationFrame(function() {
            if (typeof window.adjustHeaderFonts === 'function') {
              window.adjustHeaderFonts(true);
            }
          });
          updateBarHeight();
        });
      });
    }

    // Periodic cart sync (Shopify cart updates don't always fire DOM events)
    setInterval(function() {
      if (isMobile()) syncCartState();
    }, 2000);

    // Smart dimension sort on filter changes
    document.addEventListener('click', function(e) {
      if (e.target.closest('.cb-filter-pill, .cb-dim-tab')) {
        const collection = getActiveCollection();
        if (collection) {
          setTimeout(function() { smartSortDimensions(collection); }, 50);
        }
      }
    });
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();